import { useMemo } from 'react'
import { EstimatedPaymentCategory, EstimatedPaymentSchedule, PaymentCategory } from '@genoa/domain'
import { PaymentSchedule } from '@genoa/middle-end'
import { toFormattedDollars } from '@genoa/utils'

import { usePropertyPassthroughFee } from '../../../../../../hooks/flex2/pricing/use-property-passthrough-fee'
import { getPropertyPassthroughFeeFromPaymentSchedule, PaymentScheduleItemProps } from './usePaymentStatusToSchedule'

const handlePaymentDate = (payment: PaymentSchedule | EstimatedPaymentSchedule) => {
  if (
    payment.payment_category === PaymentCategory.FIRST_PAYMENT ||
    payment.payment_category === EstimatedPaymentCategory.FIRST_PAYMENT
  ) {
    return 'Today'
  } else {
    const date = new Date(payment.bill_date)
    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC',
      weekday: 'long',
    })
  }
}

export const usePaymentItems = (payments: (PaymentSchedule | EstimatedPaymentSchedule)[] | undefined) => {
  const propertyPassthroughFee = getPropertyPassthroughFeeFromPaymentSchedule(payments)
  const { propertyPassthroughFeeLineItem, displayPropertyPassthroughFee } =
    usePropertyPassthroughFee(propertyPassthroughFee)

  return useMemo(() => {
    if (!payments || payments?.length < 1) {
      return []
    }

    const [firstInstallment, secondInstallment] = [...payments].sort((a, b) => {
      const dateA = new Date(a.bill_date).getTime()
      const dateB = new Date(b.bill_date).getTime()
      return dateA - dateB
    })

    const firstPayment: PaymentScheduleItemProps = {
      label: 'When rent is due',
      amount: toFormattedDollars(firstInstallment.amount),
      lineItems: [{ label: 'Deposit', amount: toFormattedDollars(firstInstallment.base_amount) }],
    }

    const secondPayment: PaymentScheduleItemProps = {
      label: handlePaymentDate(secondInstallment),
      amount: toFormattedDollars(secondInstallment.amount),
      lineItems: [
        { label: 'Payment', amount: toFormattedDollars(secondInstallment.base_amount) },
        {
          label: `${secondInstallment.processing_fee_percent}% monthly rate`,
          amount: toFormattedDollars(secondInstallment.processing_fee),
        },
        ...(displayPropertyPassthroughFee ? [propertyPassthroughFeeLineItem] : []),
      ],
    }

    return [firstPayment, secondPayment]
  }, [payments])
}
